import React, { useState, useImperativeHandle } from 'react';
import {
  Form,
  Input,
  Row,
  Col,
  Button,
  Checkbox,
  Tooltip,
  Divider,
  Alert,
  Collapse,
  Modal,
} from 'antd';
import {
  QuestionCircleOutlined,
} from '@ant-design/icons';

import StrikeTeamDetailsModel from '../../models/StrikeTeamDetailsModel';
import {
  DefaultTeamPermissions, AdminTeamPermissions, FormItemLayout, TailFormItemLayout,
} from '../../utils/Constants';

import {
  tailFormItemLayout,
  TeamEditReviewDetailsFormItem,
} from './EditTeamForm';

export const EditTeamReviewSettingsForm = ({ onUpdated, team, teamId, ...rest }) => {
  const [form] = Form.useForm();

  const onFinish = (values) => {
    const capturedValues = { ...values };
    const successReviewDetails = capturedValues.reviewDetails ? capturedValues.reviewDetails.success : null;
    if (successReviewDetails) {
      capturedValues.reviewDetails.success = {
        url: successReviewDetails.url || null,
        title: successReviewDetails.title || null,
        button: successReviewDetails.button || null,
      };
    }
    if (onUpdated) {
      onUpdated(capturedValues);
    }
  };

  return (
    <Form
      {...FormItemLayout}
      form={form}
      name="teamNameSettingsForm"
      onFinish={onFinish}
      initialValues={{ ...team }}
      scrollToFirstError
    >
      <Input.Group style={{ textAlign: 'left' }}>
        <TeamEditReviewDetailsFormItem team={team} />
      </Input.Group>
      <Form.Item
        {...tailFormItemLayout}
      >
        <Button
          type="primary"
          htmlType="submit"
          style={{ width: 'auto', maxWidth: '325px' }}
        >
          {"Save Changes"}
        </Button>
      </Form.Item>
    </Form>
  );
}

export const EditTeamReviewSettingsUpdate = ({ teamId, team, onLoading }) => {
  const teamDetailsModel = new StrikeTeamDetailsModel({
    teamId,
  });
  const [loading, setLoading] = useState(false);

  const handleUpdate = async (team) => {
    onLoading && onLoading({ message: `Saving reviews ...` });
    setLoading(true);
    await teamDetailsModel.updateReviewDetails(team);
    setLoading(false);
    onLoading && onLoading({ message: null })
  }

  return (
    <div style={{ marginTop: '12px', marginLeft: '12px' }}>
      <EditTeamReviewSettingsForm
        team={team}
        onUpdated={handleUpdate}
        teamId={teamId}
      />
    </div>
  )
}


/**
 * @review  { 
 *  displayName, description, imageUrl, brand, link, rgbColor, 
 *  status: 'created' | 'live', 
 *  type: 'review',
 *  url, title, button,
 *  campaign
 * }
 * @updatedReview { url, title, button, campaign }
 */
export const EditTeamReviewForm = ({ team, review, onUpdated, onDeactivate, isAdmin, closeCheckRef }) => {
  const [form] = Form.useForm();

  useImperativeHandle(closeCheckRef, () => ({
    canClose: () => {
      const isApp = review?.templateIsApp !== undefined ? review.templateIsApp : !!review?.isApp;
      if (isApp) return true;
      const url = form.getFieldValue?.('url') ?? form.getFieldValue?.(['url']);
      return !!(url && String(url).trim());
    },
  }), [closeCheckRef, review?.templateIsApp, review?.isApp]);
  const [utmPreviewInputs, setUtmPreviewInputs] = useState({
    campaign: review?.campaign || '',
    title: review?.title || '',
    button: review?.button || '',
  });

  const { templateIsApp, isApp: teamIsApp, displayName, description, imageUrl, brand, link, rgbColor, id, tid, sid } = review;
  const isApp = templateIsApp !== undefined ? templateIsApp : !!teamIsApp;
  const { url, title, button } = review;
  const { teamName } = team || {};
  const sanitizeForUtmPreview = (value) => String(value || '')
    .toLowerCase()
    .replace(/\s+/g, '-')
    .replace(/[^a-z0-9_-]/g, '')
    .trim();
  const defaultCampaignRaw = review?.campaign || brand || '';
  const defaultCampaign = sanitizeForUtmPreview(defaultCampaignRaw || 'review');
  const effectiveCampaign = sanitizeForUtmPreview(
    utmPreviewInputs.campaign || defaultCampaignRaw || 'review',
  );
  const effectiveContent = sanitizeForUtmPreview([displayName, teamName].filter(Boolean).join('_') || 'team');
  const effectiveTerm = sanitizeForUtmPreview(
    [utmPreviewInputs.title, utmPreviewInputs.button].filter(Boolean).join('_') || review?.type || 'review',
  );

  const onFinish = (values) => {
    const capturedValues = { ...review, ...values };
    
    // For apps, we prevent overrides of core fields
    if (isApp) {
      capturedValues.url = review.templateLink || review.link || null;
      capturedValues.title = review.displayName || review.description || null;
      capturedValues.button = review.templateButton || 'Download';
      capturedValues.rgbColor = review.templateColor || review.rgbColor || null;
      capturedValues.isApp = true;
    } else {
      capturedValues.url = capturedValues.url || null;
      capturedValues.title = capturedValues.title || null;
      capturedValues.button = capturedValues.button || null;
      capturedValues.isApp = false;
    }
    
    capturedValues.campaign = isAdmin ? (capturedValues.campaign || null) : null;
    if (onUpdated) {
      onUpdated(capturedValues);
    }
  };

  return (
    <Form
      {...FormItemLayout}
      form={form}
      name="teamEditReview"
      onFinish={onFinish}
      onValuesChange={(_, allValues) => {
        setUtmPreviewInputs({
          campaign: allValues?.campaign || '',
          title: allValues?.title || '',
          button: allValues?.button || '',
        });
      }}
      initialValues={{ ...review }}
      scrollToFirstError
    >
      <>
        <div className='card'>
          <div className="card-body">
            {isApp && (
              <Alert
                message="Mobile App"
                description="Branding and links are managed globally by the system to ensure app store compliance."
                type="info"
                showIcon
                style={{ marginBottom: '16px' }}
              />
            )}
            <div className='card-title h3'>
              {imageUrl && <img className='rounded' src={imageUrl} alt={displayName} style={{ width: '60px', height: '60px', borderRadius: isApp ? '10%' : '4px', marginRight: '12px' }} />}
              {displayName || 'Add Review'}
            </div>
            {description && <p className='card-text'>{description}</p>}
            {link && !isApp && <a href={link} className="card-link">{link}</a>}
            {link && !isApp && <div className='card-subtitle'>{'Use this website to find out how to setup your site link'}</div>}

            <Divider />

            {!isApp && (
              <>
                <Form.Item
                  name={["url"]}
                  label={
                    <span>
                      Button link&nbsp;
                      <Tooltip title="Enter a site link (required)">
                        <QuestionCircleOutlined />
                      </Tooltip>
                    </span>
                  }
                  style={{ width: '100%', textAlign: 'left', marginBottom: '5px' }}
                  rules={[
                    {
                      required: true,
                      message: 'Button link is required',
                      whitespace: true
                    },
                    {
                      validator: (_, value) =>
                        ((/^$|^(https:\/\/[a-zA-Z0-9-]+\.[a-zA-Z0-9-.]+(\/[a-zA-Z0-9-._~:\/?#[@\]!$&'()*+,;=]*)?)?$/.test(value || ''))) ?
                          Promise.resolve() :
                          Promise.reject('Please enter a valid url'),
                    },
                  ]}
                >
                  <Input
                    maxLength={140} style={{ maxWidth: '425px' }}
                    placeholder={'https://www.example.com'}
                  />
                </Form.Item>

                <Form.Item
                  name={["title"]}
                  label={
                    <span>
                      Call to action&nbsp;
                      <Tooltip title="Add a call to action">
                        <QuestionCircleOutlined />
                      </Tooltip>
                    </span>
                  }
                  style={{ width: '100%', textAlign: 'left', marginBottom: '5px' }}
                  rules={[
                    {
                      required: false,
                      message: 'Enter a short call to action message',
                    },
                    {
                      validator: (_, value) =>
                        (/^.{0,60}$/.test(value)) ?
                          Promise.resolve() :
                          Promise.reject('Please enter a shorter message'),
                    },
                  ]}
                >
                  <Input
                    maxLength={60} style={{ maxWidth: '425px' }}
                    placeholder={'Add title text'}
                  />
                </Form.Item>

                <Form.Item
                  name={["button"]}
                  label={
                    <span>
                      Button label&nbsp;
                      <Tooltip title="Add a button label">
                        <QuestionCircleOutlined />
                      </Tooltip>
                    </span>
                  }
                  style={{ width: '100%', textAlign: 'left' }}
                  rules={[
                    {
                      required: false,
                      message: 'Enter a short and simple button label',
                      whitespace: false
                    },
                    {
                      validator: (_, value) =>
                        (/(^(?=.{1,24}$)(\b\w+\b)(\s\b\w+\b)?$)|^$/.test(value)) ?
                          Promise.resolve() :
                          Promise.reject('Please enter a simple button label'),
                    },
                  ]}
                >
                  <Input
                    maxLength={24} style={{ maxWidth: '425px' }}
                    placeholder={'Add button text'}
                  />
                </Form.Item>
              </>
            )}

            {isAdmin && (
              <Form.Item
                name={["campaign"]}
                label={
                  <span>
                    Campaign ID&nbsp;
                    <Tooltip title="Add a campaign ID">
                      <QuestionCircleOutlined />
                    </Tooltip>
                  </span>
                }
                style={{ width: '100%', textAlign: 'left' }}
                rules={[
                  {
                    required: false,
                    message: 'Enter a campaign ID for utm tracking',
                    whitespace: false
                  },
                  {
                    validator: (_, value) =>
                      (/^$|^[a-zA-Z0-9_-]+$/.test(value || '')) ?
                        Promise.resolve() :
                        Promise.reject('Google only uses numbers, Meta uses letters and numbers'),
                  },
                ]}
              >
                <Input
                  style={{ width: '100%', maxWidth: '425px' }}
                  placeholder={'Enter ID for tracking'}
                />
              </Form.Item>
            )}

            <div style={{
              width: '100%',
              maxWidth: '425px',
              marginTop: '-10px',
              marginBottom: '12px',
              marginLeft: 'auto',
              marginRight: 'auto',
            }}>
              <Collapse size="small">
                <Collapse.Panel header="Show UTM tracking details" key="utm-help">
                  <div style={{ fontSize: '12px', lineHeight: '1.6' }}>
                    <div>
                      <b>utm_source</b>
                      : striketip
                    </div>
                    <div>
                      <b>utm_campaign</b>
                      :
                      {' '}
                      {effectiveCampaign || 'review'}
                    </div>
                    <div>
                      <b>utm_content</b>
                      :
                      {' '}
                      {effectiveContent || 'team'}
                      {' '}
                      (identifies team and/or member or brand info)
                    </div>
                    <div>
                      <b>utm_term</b>
                      :
                      {' '}
                      {effectiveTerm || 'review'}
                      {' '}
                      (title/button context)
                    </div>
                    <div>
                      <b>uid</b>
                      :
                      {' '}
                      unique runtime ID tied to member and/or venue context
                    </div>
                    <div><b>utm_medium</b> is set by channel:</div>
                    <div>- email (clicked email)</div>
                    <div>- success (clicked on thank you page)</div>
                  </div>
                </Collapse.Panel>
              </Collapse>
            </div>

            <Form.Item>
              <Button type="primary" htmlType="submit">
                {'Save Changes'}
              </Button>
              {onDeactivate && review?.id && (
                <Button
                  type="default"
                  danger
                  style={{ marginLeft: '8px' }}
                  onClick={() => {
                    Modal.confirm({
                      title: 'Deactivate site',
                      content: 'This site will be removed from the success page. You can add it again later if needed.',
                      okText: 'Deactivate',
                      okType: 'danger',
                      cancelText: 'Cancel',
                      onOk: () => onDeactivate(review),
                    });
                  }}
                >
                  {'Deactivate'}
                </Button>
              )}
            </Form.Item>

          </div>
        </div>
      </>

    </Form>

  );
}
